if (!customElements.get('swish-court-slideshow')) {
  customElements.define('swish-court-slideshow', class extends HTMLElement {
    connectedCallback() {
      this.teardown();
      this.abortController = new AbortController();
      const options = { signal: this.abortController.signal };
      this.stage = this.querySelector('.swish-court-slideshow__stage');
      this.cards = Array.from(this.querySelectorAll('.swish-court-slideshow__card'));
      this.current = this.querySelector('[data-slide-current]');
      this.status = this.querySelector('[data-slide-status]');
      this.contentCards = this.hasAttribute('data-content-cards');
      this.dragState = null;
      this.suppressClickUntil = 0;
      if (!this.stage || !this.cards.length) return;

      this.position = this.normalizeIndex(Math.round(this.position ?? 0));
      this.targetPosition = this.position;
      this.activeIndex = this.position;
      this.mobileLayoutQuery = globalThis.matchMedia(this.contentCards
        ? '(max-width: 989.98px)'
        : '(max-width: 989.98px), (hover: none) and (pointer: coarse)');
      this.mobileLayoutQuery.addEventListener('change', () => this.render(false), options);
      this.phoneLayoutQuery = globalThis.matchMedia('(max-width: 749.98px)');
      this.phoneLayoutQuery.addEventListener('change', () => this.render(false), options);
      if (!this.contentCards) {
        this.ownerDocument.addEventListener('keydown', () => this.removeAttribute('data-pointer-input'), options);
      }
      if (this.contentCards) this.initializeContentCards();
      this.addEventListener('click', (event) => this.handleClick(event), options);
      this.addEventListener('keydown', (event) => this.handleKeydown(event), options);
      this.addEventListener('shopify:block:select', (event) => this.handleBlockSelect(event), options);
      this.stage.addEventListener('pointerdown', (event) => this.handlePointerDown(event), options);
      if (this.contentCards) {
        this.stage.addEventListener('dragstart', (event) => {
          if (this.dragState) event.preventDefault();
        }, options);
      }
      this.addEventListener('pointermove', (event) => this.handlePointerMove(event), options);
      this.addEventListener('pointerup', (event) => this.handlePointerUp(event), options);
      this.addEventListener('pointercancel', (event) => this.handlePointerCancel(event), options);
      this.addEventListener('lostpointercapture', (event) => this.handleLostPointerCapture(event), options);
      this.render(false);
      this.setAttribute('data-ready', '');
    }

    disconnectedCallback() {
      this.teardown();
    }

    teardown() {
      this.cancelAnimation();
      this.releaseDrag();
      if (this.contentMeasurementFrame != null) cancelAnimationFrame(this.contentMeasurementFrame);
      this.contentMeasurementFrame = null;
      this.contentMeasurementToken = null;
      this.contentResizeObserver?.disconnect();
      this.contentResizeObserver = null;
      this.restoreContentControls();
      this.abortController?.abort();
    }

    initializeContentCards() {
      this.contentEntries = this.cards.map((card) => ({
        card,
        inner: card.querySelector('.swish-preview-column__card')
      }));
      this.contentControlState = new Map();
      this.contentHeights = new Map();
      this.contentMeasuredWidth = null;
      this.contentVisibleSet = null;
      this.contentControlsKey = null;
      const token = {};
      this.contentMeasurementToken = token;
      const hiddenCards = this.cards.filter((card) => card.hidden);
      hiddenCards.forEach((card) => { card.hidden = false; });
      try {
        this.measureContentCards();
      } finally {
        hiddenCards.forEach((card) => { card.hidden = true; });
      }
      if (typeof ResizeObserver === 'function') {
        this.contentResizeObserver = new ResizeObserver(() => {
          if (this.contentMeasurementToken === token) this.scheduleContentMeasurement();
        });
        this.contentEntries.forEach(({ inner }) => {
          if (inner) this.contentResizeObserver.observe(inner);
        });
      }
    }

    scheduleContentMeasurement() {
      if (!this.contentMeasurementToken || !this.isConnected || this.contentMeasurementFrame != null) return;
      const token = this.contentMeasurementToken;
      this.contentMeasurementFrame = requestAnimationFrame(() => {
        this.contentMeasurementFrame = null;
        if (this.contentMeasurementToken === token) this.measureContentCards();
      });
    }

    measureContentCards() {
      if (!this.contentMeasurementToken) return;
      const measurements = [];
      this.contentEntries.forEach(({ inner }) => {
        if (!inner) return;
        const height = inner.offsetHeight;
        if (height > 0) measurements.push({ inner, height });
      });
      const width = measurements[0]?.inner.offsetWidth;
      if (width > 0) {
        if (this.contentMeasuredWidth != null && Math.abs(width - this.contentMeasuredWidth) > 0.5) this.contentHeights.clear();
        this.contentMeasuredWidth = width;
      }
      measurements.forEach(({ inner, height }) => this.contentHeights.set(inner, height));
      const height = Math.max(0, ...this.contentHeights.values());
      if (height > 0 && height !== this.contentCardHeight) {
        this.contentCardHeight = height;
        this.style.setProperty('--court-content-card-height', `${height}px`);
      }
      this.syncContentControls();
    }

    contentControlSelector() {
      return 'a[href], button, input, select, textarea, [tabindex], [contenteditable="true"], [role="button"]';
    }

    contentControl(target, card) {
      const control = target?.closest(this.contentControlSelector());
      return control && control !== card && card.contains(control) ? control : null;
    }

    syncContentControls() {
      if (!this.contentControlState) return;
      this.cards.forEach((card, index) => {
        const active = index === this.activeIndex && !card.hidden && !card.inert;
        card.querySelectorAll(this.contentControlSelector()).forEach((control) => {
          if (!this.contentControlState.has(control)) {
            this.contentControlState.set(control, {
              inert: control.inert,
              tabindex: control.getAttribute('tabindex')
            });
          }
          const original = this.contentControlState.get(control);
          control.inert = active ? original.inert : true;
          if (active) {
            if (original.tabindex == null) control.removeAttribute('tabindex');
            else control.setAttribute('tabindex', original.tabindex);
          } else control.setAttribute('tabindex', '-1');
        });
      });
    }

    restoreContentControls() {
      this.contentControlState?.forEach((original, control) => {
        control.inert = original.inert;
        if (original.tabindex == null) control.removeAttribute('tabindex');
        else control.setAttribute('tabindex', original.tabindex);
      });
      this.contentControlState = null;
    }

    normalizeIndex(index) {
      return ((index % this.cards.length) + this.cards.length) % this.cards.length;
    }

    relativePosition(index, center) {
      let relative = this.normalizeIndex(index - center);
      if (relative > this.cards.length / 2) relative -= this.cards.length;
      return relative;
    }

    select(index, { announce = true, focus = false } = {}) {
      const normalized = this.normalizeIndex(index);
      const target = normalized + Math.round((this.position - normalized) / this.cards.length) * this.cards.length;
      this.animateTo(target, { announce, focus });
    }

    move(direction) {
      const base = this.animationState ? this.targetPosition : Math.round(this.position);
      this.animateTo(base + direction);
    }

    cancelAnimation() {
      if (this.animationFrame != null) cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
      this.animationState = null;
      this.removeAttribute('data-moving');
    }

    animateTo(target, { announce = true, focus = false } = {}) {
      if (this.dragState) {
        if (this.dragState.dragging) this.suppressClickUntil = performance.now() + 500;
        this.releaseDrag();
      }
      this.cancelAnimation();
      this.targetPosition = target;
      const from = this.position;
      if (Math.abs(target - from) < 0.000001 || globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
        this.settle(target, { announce, focus });
        return;
      }

      const state = {
        from,
        target,
        start: performance.now(),
        duration: Math.min(360, 280 + Math.abs(target - from) * 30),
        announce,
        focus
      };
      this.animationState = state;
      this.setAttribute('data-moving', '');
      const frame = (time) => {
        if (this.animationState !== state) return;
        const progress = Math.max(0, Math.min(1, (time - state.start) / state.duration));
        const eased = 1 - Math.pow(1 - progress, 3);
        this.position = state.from + (state.target - state.from) * eased;
        this.render(false);
        if (progress < 1) this.animationFrame = requestAnimationFrame(frame);
        else {
          this.animationFrame = null;
          this.animationState = null;
          this.removeAttribute('data-moving');
          this.settle(state.target, state);
        }
      };
      this.animationFrame = requestAnimationFrame(frame);
    }

    settle(target, { announce = true, focus = false } = {}) {
      this.position = this.normalizeIndex(Math.round(target));
      this.targetPosition = this.position;
      this.activeIndex = this.position;
      this.render(announce);
      if (focus) this.cards[this.activeIndex].focus({ preventScroll: true });
    }

    render(announce) {
      // Phones show one image; retain the existing tablet and desktop stacks.
      const mobileImages = !this.contentCards && this.mobileLayoutQuery.matches;
      const phoneImages = !this.contentCards && this.phoneLayoutQuery.matches;
      const mobileLarge = mobileImages && this.dataset.sectionHeightMobile === 'large';
      const visibleRadius = phoneImages ? 0 : mobileImages ? 2 : 3;
      const offsets = phoneImages ? [0, 0] : mobileLarge ? [0, 0.5, 0.62, 0.69] : [0, 0.5, 0.9, 1.225, 1.475];
      const scales = phoneImages ? [1, 1] : mobileLarge ? [1, 0.24, 0.14, 0.07] : [1, 0.8, 0.65, 0.5, 0.35];
      const visualCenter = Math.round(this.position);
      this.dataset.visualPosition = this.position.toFixed(6);
      this.cards.forEach((card, index) => {
        const relative = this.relativePosition(index, this.position);
        const distance = Math.abs(relative);
        const visible = distance < visibleRadius + 1;
        const slot = this.relativePosition(index, visualCenter);
        const lower = Math.min(visibleRadius, Math.floor(distance));
        const fraction = Math.min(1, distance - lower);
        const offset = (offsets[lower] + (offsets[lower + 1] - offsets[lower]) * fraction) * Math.sign(relative);
        const scale = scales[lower] + (scales[lower + 1] - scales[lower]) * fraction;
        // Crossfade in place on phones, without shrinking or exposing side cards.
        const opacity = phoneImages
          ? relative <= 0 ? 1 : Math.max(0, 1 - distance)
          : distance <= visibleRadius ? 1 : Math.max(0, visibleRadius + 1 - distance);
        const interactive = visible && opacity > 0 && Math.abs(slot) <= visibleRadius;

        card.hidden = !visible;
        card.inert = !interactive;
        card.tabIndex = interactive ? 0 : -1;
        if (visible) {
          card.dataset.position = String(slot);
          card.style.setProperty('--court-slide-offset', `calc(var(--court-card-width) * ${offset})`);
          card.style.setProperty('--court-slide-scale', String(scale));
          card.style.setProperty('--court-slide-opacity', String(opacity));
          card.style.zIndex = String(phoneImages ? relative > 0 ? 1001 : 1000 : 1000 - Math.round(distance * 100));
        } else delete card.dataset.position;

        if (distance <= visibleRadius + 1) {
          const image = card.querySelector('img');
          if (image && image.loading !== 'eager') image.loading = 'eager';
        }
        if (index === this.activeIndex) card.setAttribute('aria-current', 'true');
        else card.removeAttribute('aria-current');
      });

      if (this.contentCards) {
        const visibleSet = this.cards.map((card, index) => !card.hidden ? index : '').join(',');
        if (visibleSet !== this.contentVisibleSet) {
          this.contentVisibleSet = visibleSet;
          this.scheduleContentMeasurement();
        }
        const active = this.cards[this.activeIndex];
        const controlsKey = `${this.activeIndex}:${active.hidden}:${active.inert}`;
        if (controlsKey !== this.contentControlsKey) {
          this.contentControlsKey = controlsKey;
          this.syncContentControls();
        }
      }

      if (this.current) this.current.textContent = String(this.activeIndex + 1);
      this.querySelectorAll('[data-slide-previous], [data-slide-next]').forEach((button) => {
        button.disabled = this.cards.length < 2;
      });
      if (announce && this.status) {
        const card = this.cards[this.activeIndex];
        const name = card.dataset.imageName || card.querySelector('img')?.alt || '';
        this.status.textContent = `Slide ${this.activeIndex + 1} of ${this.cards.length}${name ? `: ${name}` : ''}`;
      }
    }

    handleClick(event) {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      if (event.detail !== 0 && this.stage.contains(target) && performance.now() < this.suppressClickUntil) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.suppressClickUntil = 0;
        return;
      }
      const previous = target.closest('[data-slide-previous]');
      const next = target.closest('[data-slide-next]');
      if (previous && this.contains(previous)) this.move(-1);
      else if (next && this.contains(next)) this.move(1);
      else {
        const card = target.closest('.swish-court-slideshow__card');
        if (!card || !this.contains(card) || card.hidden || card.inert) return;
        const index = Number(card.dataset.slideIndex);
        if (!Number.isInteger(index) || index < 0 || index >= this.cards.length) return;
        if (this.contentCards) {
          if (index === this.activeIndex && this.contentControl(target, card)) return;
          if (index !== this.activeIndex) event.preventDefault();
        }
        this.select(index);
      }
    }

    handleKeydown(event) {
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      const target = event.target instanceof Element ? event.target : null;
      if (target?.closest('input, textarea, select, [contenteditable="true"]')) return;
      const card = target?.closest('.swish-court-slideshow__card');
      if (this.contentCards && card) {
        const index = Number(card.dataset.slideIndex);
        if (index === this.activeIndex && this.contentControl(target, card)) return;
        if (target === card && index !== this.activeIndex && ['Enter', ' ', 'Spacebar'].includes(event.key)) {
          event.preventDefault();
          this.select(index, { focus: true });
          return;
        }
      }
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      const focus = Boolean(target?.closest('.swish-court-slideshow__card'));
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        const base = this.animationState ? this.targetPosition : Math.round(this.position);
        this.animateTo(base + (event.key === 'ArrowLeft' ? -1 : 1), { focus });
      } else this.select(event.key === 'Home' ? 0 : this.cards.length - 1, { focus });
    }

    handlePointerDown(event) {
      if (!event.isPrimary || event.button !== 0 || this.dragState || this.cards.length < 2) return;
      if (!['mouse', 'touch', 'pen'].includes(event.pointerType)) return;
      if (!this.contentCards) this.setAttribute('data-pointer-input', '');
      const target = event.target instanceof Element ? event.target : null;
      const card = target?.closest('.swish-court-slideshow__card');
      if (this.contentCards && card && Number(card.dataset.slideIndex) === this.activeIndex && this.contentControl(target, card)) {
        this.suppressClickUntil = 0;
        return;
      }
      const captureTarget = card && this.stage.contains(card) ? card : this.stage;
      const cardWidth = card?.offsetWidth || this.cards.find((item) => !item.hidden)?.offsetWidth || this.stage.clientWidth;
      const animation = this.animationState;
      this.cancelAnimation();
      this.suppressClickUntil = 0;
      this.dragState = {
        id: event.pointerId,
        pointerType: event.pointerType,
        x: event.clientX,
        y: event.clientY,
        startPosition: this.position,
        stepDistance: Math.max(48, cardWidth * 0.5),
        dragging: false,
        captureTarget,
        resumeTarget: animation?.target,
        resumeOptions: animation ? { announce: animation.announce, focus: animation.focus } : null
      };
      this.capturePointer(captureTarget, this.dragState);
    }

    capturePointer(target, state) {
      try {
        target.setPointerCapture(state.id);
        state.captureTarget = target;
      } catch {
        // A pointer may end before the capture request is processed.
      }
    }

    handlePointerMove(event) {
      const state = this.dragState;
      if (!state || state.id !== event.pointerId) return;
      if (state.pointerType === 'mouse' && !(event.buttons & 1)) {
        this.finishDrag({ snap: false });
        return;
      }
      this.updateDrag(event);
    }

    updateDrag(event) {
      const state = this.dragState;
      if (!state || state.id !== event.pointerId) return;
      const horizontal = event.clientX - state.x;
      const vertical = event.clientY - state.y;
      if (!state.dragging) {
        if (Math.max(Math.abs(horizontal), Math.abs(vertical)) < 10) return;
        if (Math.abs(vertical) > Math.abs(horizontal)) {
          this.finishDrag({ snap: false });
          return;
        }
        if (Math.abs(horizontal) <= Math.abs(vertical)) return;
        state.dragging = true;
        this.setAttribute('data-dragging', '');
        this.capturePointer(this.stage, state);
      }
      if (event.cancelable) event.preventDefault();
      this.position = state.startPosition - horizontal / state.stepDistance;
      this.targetPosition = this.position;
      this.render(false);
    }

    handlePointerUp(event) {
      if (this.dragState?.id !== event.pointerId) return;
      this.updateDrag(event);
      this.finishDrag();
    }

    handlePointerCancel(event) {
      if (this.dragState?.id === event.pointerId) this.finishDrag({ snap: false });
    }

    handleLostPointerCapture(event) {
      const state = this.dragState;
      if (state?.id === event.pointerId && event.target === state.captureTarget) this.finishDrag({ snap: false });
    }

    releaseDrag() {
      const state = this.dragState;
      this.dragState = null;
      this.removeAttribute('data-dragging');
      if (!state) return;
      try {
        if (state.captureTarget.hasPointerCapture(state.id)) state.captureTarget.releasePointerCapture(state.id);
      } catch {
        // Cancellation or detachment can release capture before cleanup.
      }
    }

    finishDrag({ snap = true, announce = true } = {}) {
      const state = this.dragState;
      if (!state) return;
      if (state.dragging) this.suppressClickUntil = performance.now() + 500;
      this.releaseDrag();
      // Pointer gestures must not add a keyboard focus frame to image slides.
      if (state.dragging) this.animateTo(Math.round(this.position), { announce, focus: snap && this.contentCards });
      else if (state.resumeOptions) this.animateTo(state.resumeTarget, state.resumeOptions);
    }

    handleBlockSelect(event) {
      const target = event.target instanceof Element ? event.target : null;
      let card = target?.closest('.swish-court-slideshow__card');
      if (!card || !this.contains(card)) {
        const blockId = event.detail?.blockId;
        card = blockId ? this.cards.find((item) => item.dataset.blockId === String(blockId)) : null;
      }
      if (!card) return;
      const index = Number(card.dataset.slideIndex);
      if (Number.isInteger(index) && index >= 0 && index < this.cards.length) this.select(index, { announce: false });
    }
  });
}
